import { createContext, useContext, useMemo, type ReactNode } from "react";

import type { Line } from "../contract/types";
import { useBackend } from "../app/BackendProvider";
import { describeError, useResource } from "../app/useResource";
import { lineIconUrl } from "./lineIcons";
import { useNetworkId } from "./ConfigProvider";

interface LinesContextValue {
  lines: Line[];
  byId: (id: string) => Line | undefined;
  code: (id: string) => string;
  color: (id: string) => string;
  textColor: (id: string) => string;
  /** URL de l'icône SVG de marque de la ligne (alias Métro→M, Noctambus→N inclus). */
  iconUrl: (id: string) => string;
}

const LinesContext = createContext<LinesContextValue | null>(null);

/** Référentiel des lignes du réseau, chargé une fois et partagé. */
export function LinesProvider({ children }: { children: ReactNode }) {
  const backend = useBackend();
  const networkId = useNetworkId();
  const res = useResource((signal) => backend.api.listLines(networkId, signal), [backend, networkId]);

  const value = useMemo<LinesContextValue>(() => {
    const lines = res.data ?? [];
    const map = new Map(lines.map((l) => [l.id, l]));
    const codeOf = (id: string) => map.get(id)?.code ?? id.split(":").pop() ?? id;
    return {
      lines,
      byId: (id) => map.get(id),
      code: codeOf,
      color: (id) => map.get(id)?.color ?? "var(--sentinelle-ink-3)",
      textColor: (id) => map.get(id)?.textColor ?? "#fff",
      iconUrl: (id) => lineIconUrl(codeOf(id)),
    };
  }, [res.data]);

  if (res.loading) return <p className="p-4 has-text-grey">Chargement des lignes…</p>;
  if (res.error) {
    return <p className="notification is-danger is-light m-4">Lignes indisponibles : {describeError(res.error)}</p>;
  }

  return <LinesContext.Provider value={value}>{children}</LinesContext.Provider>;
}

export function useLines(): LinesContextValue {
  const value = useContext(LinesContext);
  if (!value) throw new Error("useLines doit être utilisé sous <LinesProvider>");
  return value;
}
